import { useEffect } from 'react';
import { userManager, beginLogoutSession } from '../OidcProvider/OidcProvider';

/**
 * FrontChannelLogout Component
 * 
 * This component is loaded when the OIDC provider calls the /logout endpoint
 * during front-channel logout (when user logs out from another app).
 * 
 * It performs the same actions as logout.html but in a React context.
 */
export function FrontChannelLogout() {
  useEffect(() => {
    console.log('[Front-Channel Logout] Processing logout notification from OIDC');
    
    const performLogout = async () => {
      try {
        beginLogoutSession();

        // Clear all OIDC session data from sessionStorage
        // Collect all keys first to avoid iteration issues
        const sessionKeys = Array.from({ length: sessionStorage.length }, (_, i) => sessionStorage.key(i));
        const keysToRemove = sessionKeys.filter(key => key?.startsWith('oidc.')) as string[];
        
        keysToRemove.forEach(key => {
          sessionStorage.removeItem(key);
          console.log('[Front-Channel Logout] Removed from sessionStorage:', key);
        });
        
        // Also clear localStorage OIDC keys if using localStorage
        const localKeys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
        const localKeysToRemove = localKeys.filter(key => key?.startsWith('oidc.')) as string[];
        
        localKeysToRemove.forEach(key => {
          localStorage.removeItem(key);
          console.log('[Front-Channel Logout] Removed from localStorage:', key);
        });
        
        console.log('[Front-Channel Logout] Session cleared successfully');
        
        // Use BroadcastChannel to notify other tabs/windows
        try {
          const channel = new BroadcastChannel('oidc-logout');
          channel.postMessage({
            type: 'frontchannel-logout',
            timestamp: Date.now()
          });
          channel.close();
          console.log('[Front-Channel Logout] Broadcast sent to other tabs');
        } catch (e) {
          console.log('[Front-Channel Logout] BroadcastChannel not supported or failed:', e);
        }
        
        // Fallback: Use localStorage event for cross-tab communication
        try {
          localStorage.setItem('oidc-logout-trigger', Date.now().toString());
          localStorage.removeItem('oidc-logout-trigger');
          console.log('[Front-Channel Logout] localStorage event triggered');
        } catch (e) {
          console.log('[Front-Channel Logout] localStorage event failed:', e);
        }
        
        // Also clear via userManager for consistency
        await userManager.removeUser().catch(err => {
          console.warn('[Front-Channel Logout] Error calling userManager.removeUser():', err);
        });
        
      } catch (error) {
        console.error('[Front-Channel Logout] Error during logout:', error);
      }
    };
    
    performLogout();
  }, []);
  
  // Return empty div - this component has no visible UI
  // It's typically loaded in a hidden iframe by the OIDC provider
  return <div data-testid="front-channel-logout" style={{ display: 'none' }} />;
}
